import React, { useEffect, useState, useCallback } from 'react';
import { Flex } from 'deprecated-enkel';
import { User } from 'lib/User';
import { Plus, Minus } from '@styled-icons/boxicons-regular';
import { ClipLoader } from 'react-spinners';

import { CardActions } from 'data/functions/card-actions';
import { ARCHIVED, MODIFIABLE_ORDER_STATES } from 'constants/states-app';
import { colors } from 'styles/theme';

import { Overlay } from 'ui/Overlay';

import { CardProductImage } from './CardProduct/CardProductImage';
import { ProductTags } from './CardProduct/ProductTags';
import { ProductBadges } from 'pages/Totem/TotemProducts/CardProduct/ProductBadges';

import { debounce, decimals, getEuros, getCents, limitToNcharacters } from 'utils';

import {
    Card,
    CardBottom,
    CardTitle,
    CardSubtitle,
    CardPackagingDisplay,
    CardDescription,
    CardContent,
    CardPriceGroup,
    CardPriceEuro,
    CardPriceCents,
    CardActionButton,
    CardButtonGroup,
    CardBottomContent,
    QuantityInputStyle,
} from 'styles/components/card.styles';
import { formatPrice } from 'utils/price';
import {
    BadgeType,
    ProductFlow,
    useAddProductMutation,
    useLessProductMutation,
    useMoreProductMutation,
    useRemoveProductMutation,
    useSetQuantityMutation,
} from 'data/__generated__';
import { CardProductImageProps } from 'pages/Totem/TotemProducts/TotemProducts.types';

interface CardProductProps extends Omit<CardProductImageProps, 'isAnalyst' | 'handleRemoveProduct' | 'productInOrder'> {
    categoryId: string;
}

type HandlerKey = 'add' | 'remove' | 'more' | 'less';

export const CardProduct: React.FC<CardProductProps> = ({
    product,
    order,
    categoryId,
    currentCategory,
    totemId,
    organization,
}) => {
    // Check if the order is modifiable based on its state
    const isOrderModifiable = order?.state ? MODIFIABLE_ORDER_STATES.includes(order.state) : false;

    // Check if caterer products are locked (catererLocked flag + product is CATERER flow)
    const isCatererProductLocked = order?.catererLocked && product.flow === ProductFlow.Caterer;

    // Product modification is disabled if order is not modifiable OR if caterer product is locked
    const isProductModificationDisabled = !isOrderModifiable || isCatererProductLocked;

    const [addProduct, addProductData] = useAddProductMutation();
    const [removeProduct, removeProductData] = useRemoveProductMutation();
    const [lessProduct, lessProductData] = useLessProductMutation();
    const [moreProduct, moreProductData] = useMoreProductMutation();
    const [setQuantity] = useSetQuantityMutation();

    const isAnalyst = User.isAnalyst(totemId ?? '');
    const handlers = {
        addProduct: {
            handler: addProduct,
            data: addProductData,
        },
        removeProduct: {
            handler: removeProduct,
            data: removeProductData,
        },
        moreProduct: {
            handler: moreProduct,
            data: moreProductData,
        },
        lessProduct: {
            handler: lessProduct,
            data: lessProductData,
        },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const delayedSetQuantity = useCallback(
        debounce((mutationOptions) => setQuantity(mutationOptions), 500),
        [],
    );

    const { handleAddProduct, handleRemoveProduct, handleMoreProduct, handleLessProduct } = CardActions(
        {
            product,
            order,
            currentCategory,
            debouncedFunction: delayedSetQuantity,
        },
        handlers,
        [],
    );

    const {
        _id: productId,
        crossedPrice,
        name,
        preferences,
        diets,
        newUntil,
        brand,
        volume,
        description,
        isWithoutStock,
        price,
        portion,
        conditionningTotem,
        weightWithoutPackaging,
        displayKg,
        isPaused,
        allowNegativeStock,
        canBeSuppliedBeforeOrderDate,
        state,
        availableStock,
    } = product;

    const productInOrder = order?.products.find(({ _id }: { _id: string }) => _id === productId);
    const isProductInOrder = !!productInOrder?.portion;

    const [quantityInput, setQuantityInput] = useState(productInOrder?.quantity);

    const synchronizeQuantity = (quantity: number) => () => {
        setQuantityInput(quantity);
    };

    const handleSetQuantity: React.ChangeEventHandler<HTMLInputElement> = ({ target: { value = 0 } }) => {
        setQuantityInput(Number(value));

        if (!value) {
            delayedSetQuantity.cancel();
            return;
        }

        delayedSetQuantity({
            variables: {
                orderId: order?._id,
                productId,
                categoryId,
                quantity: Math.round(Number(value)),
            },
            refetchQueries: [],
        });
    };

    const unitPriceNum = price;
    let unitPrice = decimals(Math.round(price * 100) / 100, 2);

    if (displayKg) {
        unitPrice = Number(unitPriceNum / (weightWithoutPackaging ?? 1)).toFixed(2);
    }

    useEffect(() => {
        setQuantityInput(productInOrder?.quantity);
    }, [productInOrder?.quantity]);

    function isLoading(keys: HandlerKey[]): boolean {
        return keys.some((key) => {
            const handlerObj = handlers[`${key}Product` as keyof typeof handlers];
            return handlerObj && handlerObj.data.loading;
        });
    }

    const quantityForecast = parseFloat((conditionningTotem / portion).toFixed(3));
    const isQtyMoreThanAvailableStock = (productInOrder?.quantity || 0) + quantityForecast > availableStock;
    const isNotEnoughStock =
        !isWithoutStock && !allowNegativeStock && !canBeSuppliedBeforeOrderDate && isQtyMoreThanAvailableStock;

    // `type` n'est pas sélectionné par PRODUCT_FRAGMENT : il est absent à l'exécution.
    const badges: Array<{ _id: string; name: string; imageUrl: string; type?: BadgeType }> = [...preferences, ...diets];
    if (newUntil) {
        // `preferences[0]?.type` valait toujours undefined (champ non sélectionné), donc ce
        // badge recevait déjà systématiquement BadgeType.Diet : comportement conservé tel quel.
        // `type` n'est de toute façon lu nulle part à l'affichage (cf. ProductBadges).
        badges.push({
            _id: `new_${product._id}`,
            name: 'Nouveau',
            imageUrl: 'img/new.svg',
            type: BadgeType.Diet,
        });
    }

    const euros = getEuros(unitPrice);
    const cents = getCents(unitPrice);
    const shouldShowPrice = parseFloat(euros) > 0 || parseInt(cents) > 0;

    return (
        <Card $noPadding data-test="product-card">
            <CardProductImage
                order={order}
                currentCategory={currentCategory}
                productInOrder={productInOrder}
                product={product}
                totemId={totemId}
                organization={organization}
                isAnalyst={!!isAnalyst || !!isProductModificationDisabled}
                handleRemoveProduct={handleRemoveProduct}
            />
            <CardBottom>
                <ProductBadges badges={badges} />
                <CardContent height="max-content" $paddingMultiplier={[0.5, 0.5, 0, 0.5]}>
                    <Flex $direction="row" $justifyContent="space-between">
                        <CardTitle $vAlign="flex-start">{limitToNcharacters(name, 36)}</CardTitle>
                        <CardPackagingDisplay>{volume}</CardPackagingDisplay>
                    </Flex>

                    <Flex $direction="row">
                        <CardSubtitle>{brand}</CardSubtitle>
                    </Flex>
                </CardContent>
                <ProductTags product={product} />
                {description && <CardDescription>{limitToNcharacters(description)}</CardDescription>}
                {shouldShowPrice && (
                    <CardBottomContent style={{ alignItems: 'flex-end', justifyContent: 'flex-end' }}>
                        <CardPriceGroup $flow="row">
                            {crossedPrice ? (
                                <CardPriceEuro $isCrossed>
                                    {formatPrice(crossedPrice, { withoutSpace: true })}
                                </CardPriceEuro>
                            ) : null}
                            <CardPriceEuro>{euros}</CardPriceEuro>
                            <CardPriceCents>
                                ,{cents}€<br />
                                {displayKg ? 'le kg' : 'la pièce'}
                            </CardPriceCents>
                        </CardPriceGroup>
                    </CardBottomContent>
                )}
                <Overlay name={`card-${productId}-overlay`} inverse />
            </CardBottom>
            {!isAnalyst && state !== ARCHIVED && !isPaused && (
                <>
                    {isProductInOrder && (
                        <CardButtonGroup $borderRadius={[false, false, true, true]} $noBorder>
                            <CardActionButton
                                $marginMultiplier={[0, 0.15, 0, 0]}
                                disabled={isLoading(['more', 'less', 'add']) || isProductModificationDisabled}
                                $isLoading={isLoading(['more', 'less', 'add'])}
                                onClick={handleLessProduct}
                                $borderRadius={[3, 3, 3, 3]}
                                data-test="product-less-button"
                            >
                                {isLoading(['less']) ? (
                                    <ClipLoader color={colors.black} size="20px" />
                                ) : (
                                    <Minus size="20px" />
                                )}
                            </CardActionButton>
                            <QuantityInputStyle
                                disabled={isProductModificationDisabled}
                                type="number"
                                value={quantityInput || ''}
                                onChange={handleSetQuantity}
                                onBlur={synchronizeQuantity(productInOrder.quantity ?? 0)}
                                data-test="quantity-input"
                            />
                            <CardActionButton
                                $marginMultiplier={[0, 0, 0, 0.15]}
                                disabled={
                                    isLoading(['more', 'less', 'add']) ||
                                    isNotEnoughStock ||
                                    isProductModificationDisabled
                                }
                                onClick={handleMoreProduct}
                                $isLoading={isLoading(['more', 'less', 'add'])}
                                $borderRadius={[3, 3, 3, 3]}
                                data-test="product-more-button"
                            >
                                {isLoading(['more']) ? (
                                    <ClipLoader color={colors.black} size="20px" />
                                ) : (
                                    <Plus size="20px" />
                                )}
                            </CardActionButton>
                        </CardButtonGroup>
                    )}
                    {!isProductInOrder && (
                        <CardButtonGroup $borderRadius={[false, false, true, true]} $noBorder>
                            <CardActionButton
                                disabled={
                                    isLoading(['more', 'less', 'add']) ||
                                    isNotEnoughStock ||
                                    isProductModificationDisabled
                                }
                                $isLoading={isLoading(['more', 'less', 'add'])}
                                onClick={handleAddProduct}
                                data-test="product-add-button"
                            >
                                {isLoading(['add']) ? <ClipLoader color={colors.black} size="20px" /> : 'Ajouter'}
                            </CardActionButton>
                        </CardButtonGroup>
                    )}
                </>
            )}
        </Card>
    );
};
